import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    FormControlLabel,
    Switch,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip
} from '@mui/material';
import {
    Save,
    Cancel,
    Add,
    Delete,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { handleFirestoreError } from '../../utils/errorHandling';
import { FIXED_EVALUATION_CATEGORIES } from '../../constants/evaluationConstants';
import {
    doc,
    getDoc,
    addDoc,
    updateDoc,
    collection,
    Timestamp
} from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { EvaluationSchema, EvaluationCategory, EvaluationCriteria } from './EvaluationSchemaList';

const defaultCriteria: EvaluationCriteria = {
    name: '',
    description: '',
    scoringMethodology: '',
    ratingScale: {
        type: 'points', // 'points' or 'percentage'
        min: 1,
        max: 5,
        labels: {
            1: 'Poor',
            2: 'Fair',
            3: 'Good',
            4: 'Very Good',
            5: 'Excellent'
        }
    },
    weight: 1.0
};

const defaultCategory: EvaluationCategory = {
    name: '',
    description: '',
    criteria: [{ ...defaultCriteria }],
    weight: 1.0
};

const defaultSchema: Omit<EvaluationSchema, 'id'> = {
    name: '',
    description: '',
    version: '1.0',
    isActive: true,
    isDefault: false,
    isGlobal: false,
    categories: FIXED_EVALUATION_CATEGORIES.map(fixedCategory => ({
        ...fixedCategory,
        weight: 1.0,
        criteria: fixedCategory.defaultCriteria.map(criterion => ({ ...criterion }))
    })),
    createdAt: null,
    updatedAt: null,
};

const EvaluationSchemaForm: React.FC = () => {
    const { user, userProfile, isGlobalAdmin } = useUser();
    const navigate = useNavigate();
    const location = useLocation();

    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);

    useDocumentTitle(isEditing ? 'Edit Evaluation Framework' : 'Create Evaluation Framework');

    const [schema, setSchema] = useState<any>(defaultSchema);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

    useEffect(() => {
        if (isEditing && id) {
            loadSchema();
        } else {
            // Check for generated schema in location state
            const state = location.state as { generatedSchema?: any };
            if (state?.generatedSchema) {
                // Determine if we are on a global route
                const isGlobalSchemaRoute = window.location.pathname.includes('/global/');

                setSchema((prev: any) => ({
                    ...prev,
                    ...state.generatedSchema,
                    // Ensure valid defaults for new schema
                    isActive: true,
                    isGlobal: isGlobalSchemaRoute || false,
                    isDefault: false,
                    // Map categories to ensure they have internal structure if needed
                    categories: state.generatedSchema.categories?.map((cat: any) => ({
                        ...cat,
                        weight: cat.weight || 1.0,
                        criteria: cat.criteria?.map((crit: any) => ({
                            ...crit,
                            weight: crit.weight || 1.0,
                            ratingScale: crit.ratingScale || defaultCriteria.ratingScale
                        }))
                    })) || []
                }));
                // Select first category
                if (state.generatedSchema.categories?.length > 0) {
                    setTimeout(() => setSelectedCategoryIndex(0), 0);
                }
            }
        }
    }, [id, isEditing, location.state]);

    const loadSchema = async () => {
        try {
            setLoading(true);
            setError('');

            // Check if this is a global schema route
            const isGlobalSchemaRoute = window.location.pathname.includes('/global/');
            const collectionName = isGlobalSchemaRoute ? 'globalEvaluationSchemas' : 'evaluationSchemas';

            let schemaDoc = await getDoc(doc(db, collectionName, id as string));

            // If not found in the expected collection, try the other one
            let actualCollection = collectionName;
            if (!schemaDoc.exists()) {
                const alternateCollection = isGlobalSchemaRoute ? 'evaluationSchemas' : 'globalEvaluationSchemas';
                schemaDoc = await getDoc(doc(db, alternateCollection, id as string));
                actualCollection = alternateCollection;
            }

            if (!schemaDoc.exists()) {
                setError('Evaluation framework not found');
                return;
            }

            const schemaData = schemaDoc.data();

            // Check permissions
            if (!isGlobalAdmin() && schemaData && schemaData.tenantId !== user?.tenantId) {
                setError('You do not have permission to edit this framework');
                return;
            }

            setSchema({ id, ...schemaData, _collection: actualCollection });

            // Set selected category to first category
            if (schemaData && schemaData.categories && schemaData.categories.length > 0) {
                setSelectedCategoryIndex(0);
            }

        } catch (error) {
            console.error('Error loading schema:', error);
            handleFirestoreError(error, 'Loading evaluation framework', setError);
        } finally {
            setLoading(false);
        }
    };

    const handleSchemaChange = (field: keyof EvaluationSchema, value: any) => {
        setSchema((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCategoryChange = (categoryIndex: number, field: keyof EvaluationCategory, value: any) => {
        setSchema((prev: any) => ({
            ...prev,
            categories: prev.categories.map((category: EvaluationCategory, index: number) =>
                index === categoryIndex ? { ...category, [field]: value } : category
            )
        }));
    };

    const handleCriteriaChange = (categoryIndex: number, criteriaIndex: number, field: keyof EvaluationCriteria, value: any) => {
        setSchema((prev: any) => ({
            ...prev,
            categories: prev.categories.map((category: EvaluationCategory, catIndex: number) =>
                catIndex === categoryIndex
                    ? {
                        ...category,
                        criteria: category.criteria.map((criteria: EvaluationCriteria, critIndex: number) =>
                            critIndex === criteriaIndex ? { ...criteria, [field]: value } : criteria
                        )
                    }
                    : category
            )
        }));
    };

    // Categories management
    const addCategory = () => {
        setSchema((prev: any) => ({
            ...prev,
            categories: [
                ...prev.categories,
                { ...defaultCategory, name: `New Category ${prev.categories.length + 1}` }
            ]
        }));
        // Select the new category
        setTimeout(() => setSelectedCategoryIndex(schema.categories.length), 0);
    };

    const removeCategory = (categoryIndex: number) => {
        if (schema.categories.length <= 1) {
            setError('At least one category is required.');
            return;
        }

        setSchema((prev: any) => {
            const newCategories = prev.categories.filter((_: any, index: number) => index !== categoryIndex);
            return {
                ...prev,
                categories: newCategories
            };
        });

        // Adjust selected index
        if (selectedCategoryIndex >= categoryIndex) {
            setSelectedCategoryIndex(Math.max(0, selectedCategoryIndex - 1));
        }
    };

    const addCriteria = (categoryIndex: number) => {
        const newCriteria = {
            ...defaultCriteria,
            name: `Criteria ${schema.categories[categoryIndex].criteria.length + 1}`
        };

        setSchema((prev: any) => ({
            ...prev,
            categories: prev.categories.map((category: EvaluationCategory, index: number) =>
                index === categoryIndex
                    ? { ...category, criteria: [...category.criteria, newCriteria] }
                    : category
            )
        }));
    };

    const removeCriteria = (categoryIndex: number, criteriaIndex: number) => {
        const currentCategory = schema.categories[categoryIndex];
        if (currentCategory.criteria.length <= 1) {
            setError('At least one criteria is required per category');
            return;
        }

        setSchema((prev: any) => ({
            ...prev,
            categories: prev.categories.map((category: EvaluationCategory, catIndex: number) =>
                catIndex === categoryIndex
                    ? {
                        ...category,
                        criteria: category.criteria.filter((_: any, critIndex: number) => critIndex !== criteriaIndex)
                    }
                    : category
            )
        }));
    };

    // Category selection management
    const handleCategorySelection = (event: any) => {
        setSelectedCategoryIndex(event.target.value);
    };

    const validateSchema = () => {
        if (!schema.name.trim()) {
            setError('Framework name is required');
            return false;
        }

        if (schema.categories.length === 0) {
            setError('At least one category is required');
            return false;
        }

        for (let i = 0; i < schema.categories.length; i++) {
            const currentCategory = schema.categories[i];
            if (!currentCategory.name.trim()) {
                setError(`Category ${i + 1} name is required`);
                return false;
            }

            if (currentCategory.criteria.length === 0) {
                setError(`Category "${currentCategory.name}" must have at least one criteria`);
                return false;
            }

            for (let j = 0; j < currentCategory.criteria.length; j++) {
                const criteria = currentCategory.criteria[j];
                if (!criteria.name.trim()) {
                    setError(`Criteria ${j + 1} in category "${currentCategory.name}" name is required`);
                    return false;
                }
            }
        }

        return true;
    };

    const handleSave = async () => {
        if (!user) return;
        if (!validateSchema()) {
            return;
        }

        try {
            setSaving(true);
            setError('');

            const schemaData = {
                ...schema,
                tenantId: isGlobalAdmin() && schema.isGlobal ? null : user.tenantId,
                updatedAt: Timestamp.now(),
                updatedBy: {
                    uid: user.uid,
                    email: user.email,
                    displayName: userProfile?.displayName || user.email
                }
            };

            if (isEditing && id) {
                const collectionName = schema._collection || 'evaluationSchemas';
                await updateDoc(doc(db, collectionName, id as string), schemaData);
            } else {
                schemaData.createdAt = Timestamp.now();
                schemaData.createdBy = schemaData.updatedBy;
                const collectionName = (isGlobalAdmin() && schema.isGlobal) ? 'globalEvaluationSchemas' : 'evaluationSchemas';
                await addDoc(collection(db, collectionName), schemaData);
            }

            navigate('/evaluation-schemas');
        } catch (error) {
            console.error('Error saving schema:', error);
            handleFirestoreError(error, 'Saving evaluation framework', setError);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/evaluation-schemas');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        {isEditing ? 'Edit Evaluation Framework' : 'Create Evaluation Framework'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Configure evaluation criteria and scoring methodology for each category. Select a category above to customize its evaluation criteria.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        startIcon={<Cancel />}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        startIcon={<Save />}
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={20} /> : (isEditing ? 'Update' : 'Create')}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Information about customizable categories */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Custom Evaluation Categories</strong><br />
                You can customize, add, or remove evaluation categories. The system includes standard categories by default, but you can modify them to match your specific needs.
            </Alert>

            {/* Framework Basic Info */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Framework Information
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Framework Name"
                                value={schema.name}
                                onChange={(e) => handleSchemaChange('name', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                                fullWidth
                                label="Version"
                                value={schema.version}
                                onChange={(e) => handleSchemaChange('version', e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 12 }}>
                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={5}
                                value={schema.description}
                                onChange={(e) => handleSchemaChange('description', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={schema.isActive}
                                            onChange={(e) => handleSchemaChange('isActive', e.target.checked)}
                                        />
                                    }
                                    label="Active"
                                />
                                {isGlobalAdmin() && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={schema.isGlobal || false}
                                                onChange={(e) => handleSchemaChange('isGlobal', e.target.checked)}
                                            />
                                        }
                                        label="Global Framework"
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Categories with Dropdown Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6">
                            Evaluation Categories ({schema.categories.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 250 }}>
                                <InputLabel>Select Category to Edit</InputLabel>
                                <Select
                                    value={selectedCategoryIndex}
                                    onChange={handleCategorySelection}
                                    label="Select Category to Edit"
                                >
                                    {schema.categories.map((category: EvaluationCategory, index: number) => (
                                        <MenuItem key={index} value={index}>
                                            {category.name} ({category.criteria.length} criteria)
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={addCategory}
                            >
                                Add Category
                            </Button>
                        </Box>
                    </Box>

                    {/* Category Information Header */}
                    {schema.categories[selectedCategoryIndex] && (
                        <Card variant="outlined" sx={{ mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            {schema.categories[selectedCategoryIndex].name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {schema.categories[selectedCategoryIndex].description}
                                        </Typography>
                                        <Chip
                                            label={`${schema.categories[selectedCategoryIndex].criteria.length} criteria defined`}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>
                                    <Button
                                        color="error"
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={() => removeCategory(selectedCategoryIndex)}
                                        disabled={schema.categories.length <= 1}
                                    >
                                        Remove Category
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Selected Category Details */}
                    {schema.categories[selectedCategoryIndex] && (
                        <Box>
                            {/* Category Settings */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50', borderRadius: 1 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Category Settings
                                </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Category Name"
                                            value={schema.categories[selectedCategoryIndex].name}
                                            onChange={(e) => handleCategoryChange(selectedCategoryIndex, 'name', e.target.value)}
                                            required
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Category Description"
                                            multiline
                                            rows={4}
                                            value={schema.categories[selectedCategoryIndex].description}
                                            onChange={(e) => handleCategoryChange(selectedCategoryIndex, 'description', e.target.value)}
                                            helperText="Describe what this category evaluates"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: (theme) => theme.palette.text.primary,
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 3 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Category Weight"
                                            type="number"
                                            inputProps={{ min: 0, step: 0.1 }}
                                            value={schema.categories[selectedCategoryIndex].weight}
                                            onChange={(e) => handleCategoryChange(selectedCategoryIndex, 'weight', parseFloat(e.target.value) || 1.0)}
                                            helperText="Importance multiplier"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Criteria Table */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1">
                                    Evaluation Criteria ({schema.categories[selectedCategoryIndex].criteria.length})
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => addCriteria(selectedCategoryIndex)}
                                    variant="contained"
                                >
                                    Add Criteria
                                </Button>
                            </Box>

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '8px 6px' } }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.100' }}>
                                            <TableCell sx={{ width: '20%', padding: '8px 6px' }}><strong>Criteria Name</strong></TableCell>
                                            <TableCell sx={{ width: '27%', padding: '8px 6px' }}><strong>Description</strong></TableCell>
                                            <TableCell sx={{ width: '27%', padding: '8px 6px' }}><strong>Scoring Methodology</strong></TableCell>
                                            <TableCell sx={{ width: '9%', padding: '8px 6px' }} align="center"><strong>Weight</strong></TableCell>
                                            <TableCell sx={{ width: '8%', padding: '8px 6px' }} align="center"><strong>Rating Scale</strong></TableCell>
                                            <TableCell sx={{ width: '9%', padding: '8px 6px' }} align="center"><strong>Actions</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {schema.categories[selectedCategoryIndex].criteria.map((criteria: EvaluationCriteria, criteriaIndex: number) => (
                                            <TableRow key={criteriaIndex} hover>
                                                <TableCell sx={{ padding: '8px 6px' }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        multiline
                                                        maxRows={3}
                                                        value={criteria.name}
                                                        onChange={(e) => handleCriteriaChange(selectedCategoryIndex, criteriaIndex, 'name', e.target.value)}
                                                        placeholder="Enter criteria name"
                                                        required
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                                padding: '4px 8px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: (theme) => theme.palette.text.primary,
                                                                fontSize: '0.875rem',
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ padding: '8px 6px' }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        multiline
                                                        rows={5}
                                                        value={criteria.description}
                                                        onChange={(e) => handleCriteriaChange(selectedCategoryIndex, criteriaIndex, 'description', e.target.value)}
                                                        placeholder="Brief description"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                                padding: '4px 8px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: (theme) => theme.palette.text.primary,
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ padding: '8px 6px' }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        multiline
                                                        rows={5}
                                                        value={criteria.scoringMethodology}
                                                        onChange={(e) => handleCriteriaChange(selectedCategoryIndex, criteriaIndex, 'scoringMethodology', e.target.value)}
                                                        placeholder="How should this criteria be evaluated? Include specific guidelines, examples, or scoring rules..."
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                                padding: '4px 8px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: (theme) => theme.palette.text.primary,
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: '8px 6px' }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        inputProps={{ min: 0, step: 0.1, style: { textAlign: 'center' } }}
                                                        value={criteria.weight}
                                                        onChange={(e) => handleCriteriaChange(selectedCategoryIndex, criteriaIndex, 'weight', parseFloat(e.target.value) || 1.0)}
                                                        sx={{
                                                            width: 85,
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                                                padding: '4px 8px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: (theme) => theme.palette.text.primary,
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: '8px 6px' }}>
                                                    <Chip
                                                        label={`${criteria.ratingScale?.min || 1}-${criteria.ratingScale?.max || 5}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            minWidth: 55,
                                                            fontSize: '0.7rem',
                                                            height: 28
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: '8px 6px' }}>
                                                    {schema.categories[selectedCategoryIndex].criteria.length > 1 && (
                                                        <Tooltip title="Delete Criteria">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => removeCriteria(selectedCategoryIndex, criteriaIndex)}
                                                                sx={{ padding: '4px' }}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default EvaluationSchemaForm;
